import { useEffect, useMemo, useState } from "react";
import ProfileStep from "./ProfileStep";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import "./style.css";
import VerifyIdentity from "../../Forms/VerifyIdentity";
import TaxInformation from "../../Forms/TaxInformation";
import BillingRates from "../../Forms/BillingRates";
import Resume from "../../Forms/Resume";
import Insurance from "../../Forms/Insurance";
import ProfileDetails from "../../Forms/ProfileDetails";
import { fetchQualificationTypes } from "../../../../services/api/qualification";
import { useQuery } from "@tanstack/react-query";
import { fetchUserQualificationsByType, fetchUserSkills } from "../../../../services/api/user";
import UpdateSettings from "../../../CompanySettings/Settings/CreateEditCompanySettings";
import CustomerProfileStep from "./CustomerProfileStep";
import CustomerAccountStep from "./CustomerAccountStep";

const STEP_PANELS = {
  "Verify your Identity": "panel1",
  "Tax Information": "panel2",
  "Billing rates": "panel3",
  Resume: "panel4",
  Insurance: "panel5",
  "Profile Details": "panel6",
  "Company Profile": "panel1",
};

const CompleteProfileSteps = ({ userData, refetchMe, isUserLoading, setIsAllowToSubmit }) => {
  const [expanded, setExpanded] = useState("panel1");
  const {
    isLoading: isQualificationsTypesLoading,
    error: isQualificationsTypesError,
    data: qualificationsTypesData,
  } = useQuery({
    queryKey: ["qualificationsTypes"],
    queryFn: fetchQualificationTypes,
  });

  const qualificationTypeMap = useMemo(() => {
    if (!qualificationsTypesData?.data?.data) return {};
    return qualificationsTypesData?.data?.data?.reduce((acc, item) => {
      acc[item.name.toLowerCase()] = item.id;
      return acc;
    }, {});
  }, [qualificationsTypesData]);

  // Extra data needed to know whether the Insurance and Profile Details sections
  // are complete (these are not part of userData).
  const insuranceId = qualificationTypeMap["insurance"];
  const { data: insurancesData } = useQuery({
    queryKey: ["insurances"],
    queryFn: () => fetchUserQualificationsByType(userData?.id, insuranceId),
    enabled: !!userData?.id && !!insuranceId,
  });
  const { data: userSkillsData } = useQuery({
    queryKey: ["userSkillsData"],
    queryFn: () => fetchUserSkills(userData?.id),
    enabled: !!userData?.id,
  });

  // Per-section list of still-missing required fields. Empty array => section complete.
  const missingByStep = useMemo(() => {
    const addr = userData?.personalAddress || {};
    const rates = userData?.billingRates || [];

    const identity = [];
    if (!userData?.nationality) identity.push("Nationality");
    if (!userData?.identityDocuments?.length) identity.push("ID document");
    if (!addr.addressLine1) identity.push("Address");
    if (!addr.country) identity.push("Country");
    if (!addr.city) identity.push("City");
    if (!addr.state) identity.push("State");

    const tax = [];
    // Complete when either an ABN is provided or the foreign-contractor exemption is declared.
    if (!userData?.australianBusinessNumber && !userData?.isForeignContractor)
      tax.push("ABN or Foreign Contractor declaration");

    const billing = [];
    if (!rates.length || !rates.every((r) => Number(r.rate) > 0))
      billing.push("Hourly, Half Day & Full Day rates");

    const resume = [];
    if (!userData?.resume) resume.push("Resume");

    const insurance = [];
    if (!insurancesData?.data?.length) insurance.push("Insurance policy");

    const profile = [];
    if (!userData?.picture) profile.push("Profile picture");
    if (!userData?.description) profile.push("About me");
    if (!userSkillsData?.data?.length) profile.push("At least one skill");

    return {
      "Verify your Identity": identity,
      "Tax Information": tax,
      "Billing rates": billing,
      Resume: resume,
      Insurance: insurance,
      "Profile Details": profile,
    };
  }, [userData, insurancesData, userSkillsData]);

  // Sections that are not required for profile completion get an "Optional" pill.
  const OPTIONAL_STEPS = new Set(["Insurance", "Profile Details"]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Tracks which sections have unsaved edits (reported up by the section forms).
  const [dirtySteps, setDirtySteps] = useState({});
  const setStepDirty = (label, dirty) =>
    setDirtySteps((prev) => (prev[label] === dirty ? prev : { ...prev, [label]: dirty }));

  // The company-profile step (freelance company owner / client) is backed by
  // CreateEditCompanySettings, which reports its own completion + dirty state.
  const [companyMissing, setCompanyMissing] = useState(null);

  // The customer's two steps report separately; Submit waits on both.
  const [customerMissing, setCustomerMissing] = useState(null);
  const [customerAccountMissing, setCustomerAccountMissing] = useState(null);

  useEffect(() => {
    if (userData?.role?.role !== "CUSTOMER") return;
    setIsAllowToSubmit(customerMissing?.length === 0 && customerAccountMissing?.length === 0);
  }, [userData, customerMissing, customerAccountMissing, setIsAllowToSubmit]);

  const renderCustomerStep = (label, panel, missing) => (
    <ProfileStep
      step={label}
      complete={missing !== null && missing.length === 0}
      missingFields={missing || []}
      dirty={!!dirtySteps[label]}
      active={expanded === panel}
      onClick={() => setExpanded(panel)}
    />
  );
  const renderCompanyStep = (label) => (
    <ProfileStep
      step={label}
      complete={companyMissing !== null && companyMissing.length === 0}
      missingFields={companyMissing || []}
      dirty={!!dirtySteps[label]}
      active={expanded === "panel1"}
      onClick={() => setExpanded("panel1")}
    />
  );

  // Renders a clickable left-nav step with a completion-aware checkmark + tooltip.
  const renderStep = (label) => {
    const missing = missingByStep[label] || [];
    const panel = STEP_PANELS[label];
    return (
      <ProfileStep
        step={label}
        complete={missing.length === 0}
        missingFields={missing}
        dirty={!!dirtySteps[label]}
        active={expanded === panel}
        optional={OPTIONAL_STEPS.has(label)}
        onClick={() => setExpanded(panel)}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mt-5">
      <div className="xl:col-span-3 col-span-4 lg:flex hidden flex-col ">
        {userData?.role?.role === "FREELANCER" &&
          !userData?.isCompanyOwner &&
          !userData?.isCompanyMember && (
            <>
              {renderStep("Verify your Identity")}
              {renderStep("Tax Information")}
              {renderStep("Billing rates")}
              {renderStep("Resume")}
              {renderStep("Insurance")}
              {renderStep("Profile Details")}
            </>
          )}
        {userData?.role?.role === "FREELANCER" &&
          !userData?.isCompanyOwner &&
          userData?.isCompanyMember && (
            <>
              {renderStep("Verify your Identity")}
              {renderStep("Tax Information")}
              {/* <ProfileStep step="Bank Details" /> */}
              {renderStep("Insurance")}
              {renderStep("Profile Details")}
            </>
          )}
        {userData?.role?.role === "FREELANCER" &&
          userData?.isCompanyOwner &&
          renderCompanyStep("Company Profile")}
        {userData?.role?.role === "CLIENT" && renderCompanyStep("Profile Details")}
        {userData?.role?.role === "CUSTOMER" && (
          <>
            {renderCustomerStep("Company Details", "panel1", customerMissing)}
            {renderCustomerStep("Accounts", "panel2", customerAccountMissing)}
          </>
        )}
      </div>
      <section className="xl:col-span-9 lg:col-span-8  col-span-12">
        {userData?.role?.role === "FREELANCER" && userData?.isCompanyOwner && (
          <>
            <Accordion
              expanded={expanded === "panel1"}
              onChange={handleChange("panel1")}
              className={`rounded-xl mb-4 shadow`}
              sx={{ boxShadow: "none" }}
            >
              <AccordionSummary
                sx={{ boxShadow: "none" }}
                expandIcon={
                  <i
                    className={`fa-solid fa-chevron-down ${expanded === "panel1" && "text-white"}`}
                  ></i>
                }
                aria-controls="panel1-content"
                id="panel1-header"
                className={`rounded-xl px-4`}
              >
                <h6 className={`font-normal`}>Company Profile</h6>
              </AccordionSummary>
              <AccordionDetails>
                <UpdateSettings
                  completeProfile={true}
                  setIsAllowToSubmit={setIsAllowToSubmit}
                  isFreelanceCompany={true}
                  onDirtyChange={(d) => setStepDirty("Company Profile", d)}
                  onCompletionChange={setCompanyMissing}
                />
              </AccordionDetails>
            </Accordion>
          </>
        )}
        {userData?.role?.role === "FREELANCER" &&
          !userData?.isCompanyOwner &&
          !userData?.isCompanyMember && (
            <>
              <Accordion
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
                className={`rounded-xl mb-4 shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel1" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel1-content"
                  id="panel1-header"
                  className={`rounded-xl px-4`}
                >
                  <h6 className={`font-normal`}>Verify your Identity</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <VerifyIdentity
                    refetchMe={refetchMe}
                    address={userData?.personalAddress}
                    identityDocuments={userData?.identityDocuments}
                    isUserLoading={isUserLoading}
                    nationality={userData?.nationality}
                    setIsAllowToSubmit={setIsAllowToSubmit}
                    userBillingRates={userData?.billingRates}
                    onDirtyChange={(d) => setStepDirty("Verify your Identity", d)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
                className={`rounded-xl shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel2" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel2-content"
                  id="panel2-header"
                  className={`rounded-xl  px-4`}
                >
                  <h6 className={`font-normal`}>Tax Information</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <TaxInformation
                    taxInformation={userData?.australianBusinessNumber}
                    gstPercentage={userData?.gstPercentage}
                    foreignContractor={userData?.isForeignContractor}
                    reFetchUser={refetchMe}
                    onDirtyChange={(d) => setStepDirty("Tax Information", d)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel3"}
                onChange={handleChange("panel3")}
                className={`rounded-xl shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel3" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel3-content"
                  id="panel3-header"
                  className={`rounded-xl  px-4`}
                >
                  <h6 className={`font-normal`}>Billing rates</h6>
                </AccordionSummary>
                <AccordionDetails>
                  {userData?.personalAddress?.addressLine1 ? (
                    <BillingRates
                      userBillingRates={userData?.billingRates}
                      userId={userData?.id}
                      address={userData?.personalAddress}
                      setIsAllowToSubmit={setIsAllowToSubmit}
                      refetchMe={refetchMe}
                      onDirtyChange={(d) => setStepDirty("Billing rates", d)}
                    />
                  ) : (
                    <h2 className=" mt-2 font-normal !text-red-600">
                      Please provide your "Home Address" to set billing rates.
                    </h2>
                  )}
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel4"}
                onChange={handleChange("panel4")}
                className={`rounded-xl shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel4" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel4-content"
                  id="panel4-header"
                  className={`rounded-xl  px-4`}
                >
                  <h6 className={`font-normal`}>Resume</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <Resume userId={userData?.id} resume={userData?.resume} refetchMe={refetchMe} />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel5"}
                onChange={handleChange("panel5")}
                className={`rounded-xl shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel5" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel5-content"
                  id="panel5-header"
                  className={`rounded-xl  px-4`}
                >
                  <h6 className={`font-normal`}>
                    Insurance
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 rounded-full px-2 py-[2px]">
                      Optional
                    </span>
                  </h6>
                </AccordionSummary>
                <AccordionDetails>
                  <Insurance
                    userId={userData?.id}
                    insuranceId={qualificationTypeMap["insurance"]}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel6"}
                onChange={handleChange("panel6")}
                className={`rounded-xl shadow`}
                sx={{
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  sx={{
                    boxShadow: "none",
                  }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel6" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel6-content"
                  id="panel6-header"
                  className={`rounded-xl  px-4`}
                >
                  <h6 className={`font-normal`}>
                    Profile Details
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 rounded-full px-2 py-[2px]">
                      Optional
                    </span>
                  </h6>
                </AccordionSummary>
                <AccordionDetails>
                  <ProfileDetails
                    userData={userData}
                    refetchMe={refetchMe}
                    qualificationTypeMap={qualificationTypeMap}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}
        {userData?.role?.role === "FREELANCER" &&
          !userData?.isCompanyOwner &&
          userData?.isCompanyMember && (
            <>
              <Accordion
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
                className={`rounded-xl mb-4 shadow`}
                sx={{ boxShadow: "none" }}
              >
                <AccordionSummary
                  sx={{ boxShadow: "none" }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel1" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel1-content"
                  id="panel1-header"
                  className={`rounded-xl px-4`}
                >
                  <h6 className={`font-normal`}>Verify your Identity</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <VerifyIdentity
                    refetchMe={refetchMe}
                    address={userData?.personalAddress}
                    identityDocuments={userData?.identityDocuments}
                    isUserLoading={isUserLoading}
                    nationality={userData?.nationality}
                    setIsAllowToSubmit={setIsAllowToSubmit}
                    userBillingRates={userData?.billingRates}
                    onDirtyChange={(d) => setStepDirty("Verify your Identity", d)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
                className={`rounded-xl shadow`}
                sx={{ boxShadow: "none" }}
              >
                <AccordionSummary
                  sx={{ boxShadow: "none" }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel2" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel2-content"
                  id="panel2-header"
                  className={`rounded-xl px-4`}
                >
                  <h6 className={`font-normal`}>Tax Information</h6>
                </AccordionSummary>
                <AccordionDetails>
                  <TaxInformation
                    taxInformation={userData?.australianBusinessNumber}
                    gstPercentage={userData?.gstPercentage}
                    foreignContractor={userData?.isForeignContractor}
                    reFetchUser={refetchMe}
                    onDirtyChange={(d) => setStepDirty("Tax Information", d)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel5"}
                onChange={handleChange("panel5")}
                className={`rounded-xl shadow`}
                sx={{ boxShadow: "none" }}
              >
                <AccordionSummary
                  sx={{ boxShadow: "none" }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel5" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel5-content"
                  id="panel5-header"
                  className={`rounded-xl px-4`}
                >
                  <h6 className={`font-normal`}>
                    Insurance
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 rounded-full px-2 py-[2px]">
                      Optional
                    </span>
                  </h6>
                </AccordionSummary>
                <AccordionDetails>
                  <Insurance
                    userId={userData?.id}
                    insuranceId={qualificationTypeMap["insurance"]}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion
                expanded={expanded === "panel6"}
                onChange={handleChange("panel6")}
                className={`rounded-xl shadow`}
                sx={{ boxShadow: "none" }}
              >
                <AccordionSummary
                  sx={{ boxShadow: "none" }}
                  expandIcon={
                    <i
                      className={`fa-solid fa-chevron-down ${expanded === "panel6" && "text-white "}`}
                    ></i>
                  }
                  aria-controls="panel6-content"
                  id="panel6-header"
                  className={`rounded-xl px-4`}
                >
                  <h6 className={`font-normal`}>
                    Profile Details
                    <span className="ml-2 text-xs font-normal text-gray-500 bg-gray-100 rounded-full px-2 py-[2px]">
                      Optional
                    </span>
                  </h6>
                </AccordionSummary>
                <AccordionDetails>
                  <ProfileDetails
                    userData={userData}
                    refetchMe={refetchMe}
                    qualificationTypeMap={qualificationTypeMap}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}
        {userData?.role?.role === "CLIENT" && (
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
            className={`rounded-xl mb-4 shadow`}
            sx={{
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              sx={{
                boxShadow: "none",
              }}
              expandIcon={
                <i
                  className={`fa-solid fa-chevron-down ${expanded === "panel1" && "text-white "}`}
                ></i>
              }
              aria-controls="panel1-content"
              id="panel1-header"
              className={`rounded-xl px-4`}
            >
              <h6 className={`font-normal`}>Verify your Identity</h6>
            </AccordionSummary>
            <AccordionDetails>
              <UpdateSettings
                completeProfile={true}
                setIsAllowToSubmit={setIsAllowToSubmit}
                onDirtyChange={(d) => setStepDirty("Profile Details", d)}
                onCompletionChange={setCompanyMissing}
              />
            </AccordionDetails>
          </Accordion>
        )}
        {userData?.role?.role === "CUSTOMER" && (
          <Accordion
            expanded={expanded === "panel1"}
            onChange={handleChange("panel1")}
            className={`rounded-xl mb-4 shadow`}
            sx={{ boxShadow: "none" }}
          >
            <AccordionSummary
              sx={{ boxShadow: "none" }}
              expandIcon={
                <i
                  className={`fa-solid fa-chevron-down ${expanded === "panel1" && "text-white "}`}
                ></i>
              }
              aria-controls="panel1-content"
              id="panel1-header"
              className={`rounded-xl px-4`}
            >
              <h6 className={`font-normal`}>Company Details</h6>
            </AccordionSummary>
            <AccordionDetails>
              <CustomerProfileStep
                customerId={userData?.customerId}
                onDirtyChange={(d) => setStepDirty("Company Details", d)}
                onCompletionChange={setCustomerMissing}
              />
            </AccordionDetails>
          </Accordion>
        )}
        {userData?.role?.role === "CUSTOMER" && (
          <Accordion
            expanded={expanded === "panel2"}
            onChange={handleChange("panel2")}
            className={`rounded-xl mb-4 shadow`}
            sx={{ boxShadow: "none" }}
          >
            <AccordionSummary
              sx={{ boxShadow: "none" }}
              expandIcon={
                <i
                  className={`fa-solid fa-chevron-down ${expanded === "panel2" && "text-white "}`}
                ></i>
              }
              aria-controls="panel2-content"
              id="panel2-header"
              className={`rounded-xl px-4`}
            >
              <h6 className={`font-normal`}>Accounts</h6>
            </AccordionSummary>
            <AccordionDetails>
              <CustomerAccountStep
                customerId={userData?.customerId}
                onDirtyChange={(d) => setStepDirty("Accounts", d)}
                onCompletionChange={setCustomerAccountMissing}
              />
            </AccordionDetails>
          </Accordion>
        )}
      </section>
    </div>
  );
};

export default CompleteProfileSteps;
